import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button, Box, MenuItem, TextField, Paper, Typography, IconButton } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Header } from '~/components';
import { observer, useStores } from '~/utils';
import {
  Content,
  Childrens,
  SectionTitle,
  Title,
  WrapperContent,
} from './config.styles';
import CardConfigForm from '../ConfigForm/CardProcessConfig';
import { request } from '~/services/request';

interface DataCard {
  id: string;
  name: string;
  description: string;
  columns: string[];
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ConfigForm: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const [token, setToken] = useState<string>('');
  const { user } = useStores();
  const [dataCards, setDataCards] = useState<DataCard[]>([]);
  const [newData, setNewData] = useState({
    name: '',
    group: [] as string[], // Aceitar múltiplos grupos
    description: '',
    csv_file: null as File | null,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => {
    const getToken = async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (idToken) {
          setToken(idToken);
        }
      } catch (error) {
        console.error('Erro ao obter token:', error);
        navigate('/login');
      }
    };
    getToken();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const response = await request.get(`/test_data`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDataCards(response.data);
    } catch (error) {
      console.error('Erro ao obter dados:', error);
    }
  };

  const createData = async () => {
    const formData = new FormData();
    formData.append('name', newData.name);
    newData.group.forEach((group) => formData.append('group', group));
    formData.append('description', newData.description || 'Teste');
    if (newData.csv_file) {
      formData.append('csv_file', newData.csv_file);
    }

    try {
      const response = await request.post(`/test_data`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      alert(`Massa de dados ${newData.name} adicionada com sucesso.`);
      fetchData();

      setNewData({
        name: '',
        group: [],
        description: '',
        csv_file: null,
      });
    } catch (error) {
      console.error('Erro ao criar dados:', error);
    }
  };

  const deleteData = async (id: string) => {
    try {
      const formData = new FormData();
      formData.append('id', id);
      await request.delete(`/test_data`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: formData,
      });
      alert(`Massa de dados ${id} deletada com sucesso`);
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir dados:', error);
    }
  };

  const updateData = async () => {
    if (!editingId) return;
    const formData = new FormData();
    formData.append('name', newData.name);
    newData.group.forEach((group) => formData.append('group', group)); // Enviar grupos separadamente
    formData.append('description', newData.description || 'Teste');
    if (newData.csv_file) {
      formData.append('csv_file', newData.csv_file);
    }

    try {
      await request.patch(`/test_data/${editingId}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      alert(`Massa de dados ${editingId} atualizada com sucesso`);
      fetchData();
      setNewData({ name: '', group: [], description: '', csv_file: null });
      setIsEditing(false);
      setEditingId(null);
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
    }
  };

  const startEditing = (card: DataCard) => {
    setNewData({
      name: card.name,
      group: card.columns,
      description: card.description,
      csv_file: null,
    });
    setIsEditing(true);
    setEditingId(card.id);
  };

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  return (
    <Content>
      <Header title='TDM' />

      <Childrens>
        <SectionTitle style={{ display: 'flex', alignItems: 'center', gap: '8px', alignContent: 'space-between', flexDirection: 'row', justifyContent: 'flex-start', flexWrap: 'wrap' }}>
          <IconButton onClick={() => navigate('/')}>
            <ArrowBackIcon />
          </IconButton>
          <Title>Configuração</Title>
        </SectionTitle>

        <WrapperContent>
          <Paper
            elevation={3}
            style={{
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '300px',
              marginBottom: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold' }}>
              Criar Massa de Dados
            </Typography>
            <TextField
              label="Nome"
              value={newData.name}
              onChange={(e) => setNewData({ ...newData, name: e.target.value })}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Descrição"
              value={newData.description}
              onChange={(e) => setNewData({ ...newData, description: e.target.value })}
              fullWidth
              variant="outlined"
            />
            <input
              type="file"
              onChange={(e) =>
                setNewData({ ...newData, csv_file: e.target.files ? e.target.files[0] : null })
              }
              style={{ margin: '8px 0' }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={isEditing ? updateData : createData}
              fullWidth
              style={{ marginTop: '10px' }}
            >
              {isEditing ? 'Atualizar Massa de Dados' : 'Criar Nova Massa de Dados'}
            </Button>
          </Paper>

          <SectionTitle>
            <Title>Massas de Dados</Title>
          </SectionTitle>
          <Box
            display="grid"
            gridTemplateColumns="repeat(5, 1fr)"
            gap="16px"
          >
            {dataCards.map((card) => (
              <CardConfigForm
                key={card.id}
                title={card.name}
                description={card.description || "Sem descrição"} 
                onEditPress={() => startEditing(card)}
                onDelete={() => deleteData(card.id)}
                editMode={true}
              />
            ))}
          </Box>
        </WrapperContent>
      </Childrens>
    </Content>
  );
};

export default observer(ConfigForm);
